import network from '@/services/network';
import {
  ApiResponse,
  CreateDealRequest,
  Deal,
  DealOutcomePayload,
  DealStatus,
  FollowUp,
  Note,
  NoteFormat,
  PaginatedResponse,
  ErgebnisWandAntwort,
  PendingDealOutcome,
} from '@/types';
import { DealCalculatorSettings } from '@/types/debtFreeCalculator';
import { ErrorResponse } from '@/utils';

const getDeals = async (params = {}): Promise<PaginatedResponse<Deal>> => {
  try {
    const { data } = await network.get<PaginatedResponse<Deal>>('/deals', { params });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getClosedDeals = async (params: {
  closed_from?: string;
  closed_to?: string;
  assigned_user_id?: number;
  per_page?: number;
  page?: number;
  // Server-Sortierung (Paket 3b). `sortieren` ist ein Begriff aus der
  // BE-Whitelist (abgeschlossen | kunde | thema | berater | wert | status),
  // kein Spaltenname — alles andere lehnt der Endpunkt mit 422 ab.
  sortieren?: string;
  richtung?: 'asc' | 'desc';
  /** Slug der Abschluss-Phase (`gewonnen`/`verloren`); fehlt = kein Filter. */
  status?: string;
  suche?: string;
} = {}): Promise<PaginatedResponse<Deal>> => {
  try {
    const { data } = await network.get<PaginatedResponse<Deal>>('/deals/closed', { params });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getDeal = async (dealId: number): Promise<ApiResponse<Deal>> => {
  try {
    const { data } = await network.get<ApiResponse<Deal>>(`/deals/${dealId}`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Ergebnis-Wand (Decision #245) — faellige eigene Cases, letztes Meeting ab dem Wand-Stichtag (26.10.2026). */
const getPendingDealOutcomes = async (): Promise<ErgebnisWandAntwort<PendingDealOutcome>> => {
  try {
    const { data } = await network.get<ErgebnisWandAntwort<PendingDealOutcome>>('/deals/pending-outcomes');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Case-Frist der Ergebnis-Wand um 7 Tage verlaengern (Nachtrag #245, Alex
 * 02.11.2026). Nur zugewiesener Berater (403), hoechstens 2 je Case (422).
 */
const verlaengereErgebnisFrist = async (
  dealId: number,
): Promise<{ data: { nummer: number; neue_faelligkeit: string; verbleibend: number } }> => {
  try {
    const { data } = await network.post<{ data: { nummer: number; neue_faelligkeit: string; verbleibend: number } }>(
      `/deals/${dealId}/ergebnis-frist-verlaengern`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createDeal = async (dealData: CreateDealRequest): Promise<ApiResponse<Deal>> => {
  try {
    const { data } = await network.post<ApiResponse<Deal>>('/deals', dealData);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateDeal = async (dealId: number, dealData: Partial<Deal>): Promise<ApiResponse<Deal> & { kann_weiter_sehen?: boolean }> => {
  try {
    const { data } = await network.put<ApiResponse<Deal> & { kann_weiter_sehen?: boolean }>(`/deals/${dealId}`, dealData);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteDeal = async (dealId: number): Promise<void> => {
  try {
    await network.delete(`/deals/${dealId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Sammel-Übergabe (WV-166, 15.10.2026): dieselbe Regel wie die Case Übergabe
 * am einzelnen Case — je Case ein eigener Versuch, Ergebnis je Case.
 */
export interface SammelErgebnis {
  deal_id: number;
  kunde: string | null;
  status: 'uebergeben' | 'fehler';
  meldung: string | null;
  termine: { datum: string; ausgang: 'uebernommen' | 'abgesagt' | 'offen' }[];
}

export interface SammelVorschau {
  cases: number;
  termine_mit: number;
  absagen: { deal_id: number; kunde: string | null; datum: string }[];
  nicht_moeglich: { deal_id: number; kunde: string | null; meldung: string }[];
}

const bulkChangeOwner = async (
  dealIds: number[],
  newOwnerId: number,
): Promise<{ updated_count: number; ergebnisse: SammelErgebnis[] }> => {
  try {
    const { data } = await network.post<{ data: { updated_count: number; ergebnisse: SammelErgebnis[] } }>(
      '/deals/bulk-change-owner',
      { deal_ids: dealIds, new_owner_id: newOwnerId },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const bulkUebergabeVorschau = async (dealIds: number[], newOwnerId: number): Promise<SammelVorschau> => {
  try {
    const { data } = await network.post<{ data: SammelVorschau }>(
      '/deals/bulk-change-owner/vorschau',
      { deal_ids: dealIds, new_owner_id: newOwnerId },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getDealStatuses = async (): Promise<{ data: DealStatus[] }> => {
  try {
    const { data } = await network.get<{ data: DealStatus[] }>('/deal-statuses');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

// Notes
const getNotes = async (type: 'deals' | 'customers', parentId: number): Promise<{ data: Note[] }> => {
  try {
    const { data } = await network.get<{ data: Note[] }>(`/${type}/${parentId}/notes`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createNote = async (
  type: 'deals' | 'customers',
  parentId: number,
  content: string,
  format?: NoteFormat,
): Promise<ApiResponse<Note>> => {
  try {
    // Ohne format bleibt der Inhalt serverseitig Klartext — der sichere
    // Default fuer jeden Aufrufer, der den Editor nicht benutzt.
    const { data } = await network.post<ApiResponse<Note>>(`/${type}/${parentId}/notes`, {
      content,
      ...(format ? { content_format: format } : {}),
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateNote = async (
  noteId: number,
  content: string,
  format?: NoteFormat,
): Promise<ApiResponse<Note>> => {
  try {
    const { data } = await network.put<ApiResponse<Note>>(`/notes/${noteId}`, {
      content,
      ...(format ? { content_format: format } : {}),
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteNote = async (noteId: number): Promise<void> => {
  try {
    await network.delete(`/notes/${noteId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

// Follow-ups
const getFollowUps = async (type: 'deals' | 'customers', parentId: number): Promise<{ data: FollowUp[] }> => {
  try {
    const { data } = await network.get<{ data: FollowUp[] }>(`/${type}/${parentId}/follow-ups`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createFollowUp = async (
  type: 'deals' | 'customers',
  parentId: number,
  followUpData: { title: string; description?: string; due_date: string },
): Promise<ApiResponse<FollowUp>> => {
  try {
    const { data } = await network.post<ApiResponse<FollowUp>>(
      `/${type}/${parentId}/follow-ups`,
      followUpData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateFollowUp = async (followUpId: number, followUpData: Partial<FollowUp>): Promise<ApiResponse<FollowUp>> => {
  try {
    const { data } = await network.put<ApiResponse<FollowUp>>(
      `/follow-ups/${followUpId}`,
      followUpData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Transfer v3 (#138): Termin des uebergebenen Case uebernehmen — Neubuchung
 * im selben Slot auf den neuen Owner, alter Termin storniert. Der Kunde
 * bekommt bewusst NICHTS Aktives.
 *
 * `hinweis` ist gesetzt, wenn der alte Kalender-/Zoom-Eintrag nicht
 * storniert werden konnte (toter Token des abgebenden Beraters).
 *
 * v4 (02.08.2026): Anker ist der Termin, nicht mehr die Uebergabe-Aufgabe.
 */
const uebernehmeTransferTermin = async (
  meetingId: number,
): Promise<{ hinweis: string | null }> => {
  try {
    const { data } = await network.post<{ hinweis: string | null }>(
      `/meetings/${meetingId}/uebergabe-uebernehmen`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * „Case Übergabe" aus dem FAB-Menü — einziger Einstieg seit 15.10.2026
 * (Entscheid Alex). Ohne anstehenden Termin Owner-Wechsel wie bisher; mit
 * anstehenden Terminen prüft der Server je Termin frei/belegt: frei → Termin
 * geht mit, belegt → 409 `ziel_belegt` mit der Rückfrage, erst
 * `absageBestaetigt` sagt den belegten Termin ab.
 */
const uebergebeCase = async (
  dealId: number,
  zielUserId: number,
  absageBestaetigt?: boolean,
): Promise<{ hinweis: string | null; kann_weiter_sehen?: boolean }> => {
  try {
    const { data } = await network.post<{ hinweis: string | null; kann_weiter_sehen?: boolean }>(
      `/deals/${dealId}/uebergabe`,
      {
        ziel_user_id: zielUserId,
        ...(absageBestaetigt ? { absage_bestaetigt: true } : {}),
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Empfänger einer Case Übergabe: nur id + Name aktiver VPs (14.10.2026). */
export interface UebergabeEmpfaenger {
  id: number;
  name: string;
}

/**
 * Empfängerliste beider Übergabe-Dialoge (Entscheid Alex 14.10.2026). Ersetzt
 * dort `GET /users`, das `users.viewAny` braucht — das hat kein VP.
 */
const getUebergabeEmpfaenger = async (): Promise<UebergabeEmpfaenger[]> => {
  try {
    const { data } = await network.get<{ data: UebergabeEmpfaenger[] }>('/deals/uebergabe-empfaenger');
    return data.data ?? [];
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Eintrag der Berater-Auswahl: nur id, Name, aktiv (Entscheid Alex 20.10.2026). */
export interface BeraterAuswahlEintrag {
  id: number;
  name: string;
  is_active: boolean;
}

/**
 * Berater-Filter von Pipeline und Termine-Liste (Entscheid Alex 20.10.2026,
 * docs/tasks/pipeline-berater-filter-sicht.md). Ersetzt dort `GET /users`,
 * das `users.viewAny` braucht — das hat kein VP. Der Server begrenzt die
 * Liste auf die Sicht `case` und liefert Inaktive mit. Beide Stellen
 * bekommen dieselbe Liste — auch Admins nur VPs (Nachtrag 20.10.2026).
 */
const getBeraterAuswahl = async (): Promise<BeraterAuswahlEintrag[]> => {
  try {
    const { data } = await network.get<{ data: BeraterAuswahlEintrag[] }>(
      '/deals/berater-auswahl',
    );
    return data.data ?? [];
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Transfer v3 (#138): Termin absagen — Storno plus Kundenmail. */
const sageTransferTerminAb = async (meetingId: number): Promise<void> => {
  try {
    await network.post(`/meetings/${meetingId}/uebergabe-absagen`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteFollowUp = async (followUpId: number): Promise<void> => {
  try {
    await network.delete(`/follow-ups/${followUpId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const submitDealOutcome = async (dealId: number, data: DealOutcomePayload): Promise<ApiResponse<Deal>> => {
  try {
    const { data: response } = await network.post<ApiResponse<Deal>>(`/deals/${dealId}/outcome`, data);
    return response;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

// B-1 Welle 6 (Mailer-Merge): Manueller Mail-Versand vom Case.
/**
 * Aufgeloester Vorlagentext fuer den Editor im SendMailDialog (08.09.2026).
 * Die Aufloesung passiert EINMAL hier; was danach abgeschickt wird, geht
 * woertlich raus.
 */
const mailEntwurf = async (
  dealId: number,
  mailTemplateId: number,
): Promise<import('@/types').MailEntwurf> => {
  try {
    const { data } = await network.post<{ data: import('@/types').MailEntwurf }>(
      `/deals/${dealId}/mail-entwurf`,
      { mail_template_id: mailTemplateId },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const sendMail = async (
  dealId: number,
  payload: import('@/types').SendMailPayload,
): Promise<{ message: string }> => {
  try {
    const { data } = await network.post<{ message: string }>(
      `/deals/${dealId}/send-mail`,
      payload,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};


/**
 * Fall vor dem Zutritt zur Beratungsstrecke reaktivieren (Entscheid Alex
 * 17.08.2026, Teil 1).
 *
 * 🔥 NICHT `/deals/{id}/reactivate` — der traegt die Recycling-Regeln
 * (RC-Limit, Owner-Regeln aus #136), die auf diesem Weg ausdruecklich NICHT
 * gelten sollen.
 */
const reaktiviereFuerBeratung = async (dealId: number): Promise<ApiResponse<{ case_id: number; is_closed: boolean }>> => {
  try {
    const { data } = await network.post<ApiResponse<{ case_id: number; is_closed: boolean }>>(
      `/deals/${dealId}/reaktivieren-fuer-beratung`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Fallbezogene Rechner-Einstellungen der Beratungsstrecke (16.09.2026).
 *
 * Eigener Endpunkt statt eines Feldes in der Fall-Liste: die traegt den
 * Kanban-Payload, und dort waeren es 50 Rechner-Datensaetze je Seite, von
 * denen die Liste keinen einzigen anzeigt.
 *
 * Liefert immer ein Objekt. „Nichts gespeichert" erkennt der Aufrufer am
 * fehlenden Rechner-Schluessel, nicht an einer leeren Antwort.
 */
const getCalculatorSettings = async (
  dealId: number,
): Promise<DealCalculatorSettings> => {
  try {
    const { data } = await network.get<ApiResponse<DealCalculatorSettings>>(
      `/deals/${dealId}/calculator-settings`,
    );
    return data.data ?? {};
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const dealApi = {
  getDeals,
  getClosedDeals,
  getDeal,
  getCalculatorSettings,
  createDeal,
  updateDeal,
  deleteDeal,
  bulkChangeOwner,
  bulkUebergabeVorschau,
  getDealStatuses,
  getNotes,
  createNote,
  updateNote,
  deleteNote,
  getFollowUps,
  createFollowUp,
  updateFollowUp,
  deleteFollowUp,
  uebernehmeTransferTermin,
  sageTransferTerminAb,
  getUebergabeEmpfaenger,
  getBeraterAuswahl,
  uebergebeCase,
  submitDealOutcome,
  getPendingDealOutcomes,
  verlaengereErgebnisFrist,
  mailEntwurf,
  sendMail,
  reaktiviereFuerBeratung,
};

export default dealApi;
